import React, { useState, useCallback } from "react";
import { Card, Table, Button, Space, message, Badge, Tag, Tooltip } from "antd";
import { EyeOutlined, FileProtectOutlined } from "@ant-design/icons";
import { useLoadList } from "../../../hooks";
import { permitRequestService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import AircraftTag from "../../../components/AircraftTag";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import PermitRequestFilter from "./components/PermitRequestFilter";
import PermitRequestDetailDrawer from "./components/PermitRequestDetailDrawer";

// 状态配置映射
const STATUS_CONFIG = {
    PENDING: { status: "processing", text: "待处理", color: "orange" },
    APPROVED: { status: "success", text: "已批准", color: "green" },
    REJECTED: { status: "error", text: "已拒绝", color: "red" },
    CANCELLED: { status: "default", text: "已取消", color: "gray" },
};

/**
 * 许可申请列表页面
 */
const PermitRequestList = () => {
    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedPermitRequest, setSelectedPermitRequest] = useState(null);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("permitRequests", permitRequestService.getPermitRequestList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 查看详情
    const viewDetail = useCallback((record) => {
        setSelectedPermitRequest(record);
        setDetailDrawerVisible(true);
    }, []);

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback((values) => {
        setFilterParams(values);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    }, [pagination]);

    // 重置搜索
    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    }, [pagination]);

    // 渲染状态标签
    const renderStatusTag = (status) => {
        if (!status) {
            return <Tag color="default">无状态</Tag>;
        }
        
        const config = STATUS_CONFIG[status] || { status: "default", text: status, color: "default" };
        return <Badge status={config.status} text={config.text} />;
    };

    // 渲染许可类型摘要
    const renderPermitTypesSummary = (details) => {
        if (!details) return <span style={{ color: '#999' }}>无详情</span>;

        const types = [];
        if (details.departureLandingPermission) {
            const status = details.departureLandingPermission.status;
            const statusConfig = STATUS_CONFIG[status] || { color: "default" };
            types.push(
                <Tag key="departure" color={statusConfig.color} size="small">
                    始发起降
                </Tag>
            );
        }
        if (details.arrivalLandingPermission) {
            const status = details.arrivalLandingPermission.status;
            const statusConfig = STATUS_CONFIG[status] || { color: "default" };
            types.push(
                <Tag key="arrival" color={statusConfig.color} size="small">
                    目的起降
                </Tag>
            );
        }
        if (details.overFlightPermission) {
            const status = details.overFlightPermission.status;
            const statusConfig = STATUS_CONFIG[status] || { color: "default" };
            types.push(
                <Tag key="overflight" color={statusConfig.color} size="small">
                    飞越
                </Tag>
            );
        }

        return types.length > 0 ? <Space size={4}>{types}</Space> : <span style={{ color: '#999' }}>无许可类型</span>;
    };

    // 表格列定义
    const columns = [
        {
            title: "申请ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            fixed: "left",
        },
        {
            title: "航班信息",
            key: "flight_info",
            width: 180,
            render: (_, record) => {
                const flight = record.flight;
                if (!flight) return <span style={{ color: '#999' }}>无航班信息</span>;
                
                return (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{flight.flight_number}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {formatDateTime(flight.flight_date, 'MM-DD')} {flight.departure_airport}→{flight.arrival_airport}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "飞机",
            key: "aircraft",
            width: 100,
            render: (_, record) => {
                const flight = record.flight;
                if (!flight || !flight.aircraft_reg) {
                    return <span style={{ color: '#999' }}>未分配</span>;
                }
                return <AircraftTag registration={flight.aircraft_reg} />;
            },
        },
        {
            title: "主状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: renderStatusTag,
        },
        {
            title: "许可类型",
            key: "permit_types",
            width: 200,
            render: (_, record) => renderPermitTypesSummary(record.details),
        },
        {
            title: "批次号",
            dataIndex: "batch_id",
            key: "batch_id",
            width: 120,
            render: (text) => text || <span style={{ color: '#999' }}>无</span>,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (text) => formatDateTime(text),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 160,
            render: (text) => formatDateTime(text),
        },
        {
            title: "备注",
            dataIndex: "remarks",
            key: "remarks",
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            render: (text) => (
                text ? (
                    <Tooltip title={text}>
                        <span>{text}</span>
                    </Tooltip>
                ) : (
                    <span style={{ color: '#999' }}>无</span>
                )
            ),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 100,
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="flight_operation:read">
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => viewDetail(record)}
                        >
                            详情
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">
                    <FileProtectOutlined style={{ marginRight: 8 }} />
                    许可申请查询
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <PermitRequestFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 许可申请列表 */}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1400 }}
                />
            </Card>

            {/* 详情抽屉 */}
            <PermitRequestDetailDrawer
                visible={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                permitRequest={selectedPermitRequest}
            />
        </div>
    );
};

export default PermitRequestList; 